import type { Livre } from "./moteur/types";
import type { Partie } from "./partie";

/**
 * Ou le telephone garde la partie : le stockage local du navigateur, rien d'autre. Aucune
 * donnee ne quitte le telephone pour garder une partie, et aucun cookie n'est pose.
 *
 * Safari efface le stockage d'un site apres 7 jours sans visite, et la navigation privee peut
 * le refuser : c'est pour cela que le code de sauvegarde existe. Une ecriture refusee ne
 * casse pas la partie, qui continue dans l'onglet ; l'appli le dit au lecteur.
 */
export interface Rangement {
  lire(cle: string): string | null;
  ecrire(cle: string, valeur: string): void;
  effacer(cle: string): void;
}

export function rangementDuNavigateur(): Rangement {
  return {
    lire: (cle) => window.localStorage.getItem(cle),
    ecrire: (cle, valeur) => window.localStorage.setItem(cle, valeur),
    effacer: (cle) => window.localStorage.removeItem(cle),
  };
}

/** Un rangement en memoire : pour les tests, et quand le navigateur n'en offre pas. */
export function rangementEnMemoire(depart: Record<string, string> = {}): Rangement {
  const valeurs = new Map(Object.entries(depart));
  return {
    lire: (cle) => valeurs.get(cle) ?? null,
    ecrire: (cle, valeur) => {
      valeurs.set(cle, valeur);
    },
    effacer: (cle) => {
      valeurs.delete(cle);
    },
  };
}

export function cleDePartie(livre: Livre): string {
  return `livre-jeu:partie:${livre.id}`;
}

function entier(valeur: unknown): valeur is number {
  return typeof valeur === "number" && Number.isInteger(valeur) && valeur >= 0;
}

/**
 * Une partie gardee est-elle lisible pour ce livre ? Tout est verifie, jusqu'aux bornes des
 * jauges : une partie gardee par une version precedente du livre, ou abimee, est ecartee
 * plutot que jouee de travers.
 */
export function estPartieDe(livre: Livre, valeur: unknown): valeur is Partie {
  if (typeof valeur !== "object" || valeur === null) return false;
  const partie = valeur as Record<string, unknown>;
  if (partie.v !== 1 || partie.livre !== livre.id) return false;
  if (![partie.debut, partie.coups, partie.refus, partie.saisis].every(entier)) return false;
  if (typeof partie.finMesuree !== "boolean") return false;
  const etat = partie.etat as Record<string, unknown> | null | undefined;
  if (typeof etat !== "object" || etat === null) return false;
  if (!livre.scenes.some((scene) => scene.id === etat.scene)) return false;
  const indices = etat.indices;
  if (!Array.isArray(indices)) return false;
  if (!indices.every((id) => livre.indices.some((indice) => indice.id === id))) return false;
  const jauges = etat.jauges as Record<string, unknown> | null | undefined;
  if (typeof jauges !== "object" || jauges === null) return false;
  for (const jauge of livre.jauges) {
    const valeur = jauges[jauge.id];
    if (!entier(valeur) || valeur > jauge.max) return false;
  }
  if (Object.keys(jauges).some((id) => !livre.jauges.some((jauge) => jauge.id === id))) {
    return false;
  }
  const dernier = partie.dernier as Record<string, unknown> | null | undefined;
  if (dernier === null) return true;
  return (
    typeof dernier === "object" &&
    typeof dernier.code === "string" &&
    typeof dernier.libelle === "string" &&
    entier(dernier.page)
  );
}

export type Lue = { partie: Partie | null; illisible: boolean; indisponible: boolean };

/**
 * Lit la partie gardee. `illisible` : il y avait quelque chose, mais pas une partie de ce
 * livre. `indisponible` : le navigateur refuse son stockage.
 */
export function lirePartie(livre: Livre, rangement: Rangement): Lue {
  let brut: string | null;
  try {
    brut = rangement.lire(cleDePartie(livre));
  } catch {
    return { partie: null, illisible: false, indisponible: true };
  }
  if (brut === null) return { partie: null, illisible: false, indisponible: false };
  try {
    const valeur: unknown = JSON.parse(brut);
    if (estPartieDe(livre, valeur))
      return { partie: valeur, illisible: false, indisponible: false };
  } catch {
    // Illisible : traite plus bas, comme une partie d'une autre version du livre.
  }
  return { partie: null, illisible: true, indisponible: false };
}

/** Range la partie, ou l'efface (`null`). Rend `false` si le navigateur a refuse. */
export function ecrirePartie(livre: Livre, rangement: Rangement, partie: Partie | null): boolean {
  try {
    if (partie === null) rangement.effacer(cleDePartie(livre));
    else rangement.ecrire(cleDePartie(livre), JSON.stringify(partie));
    return true;
  } catch {
    return false;
  }
}
